<template>
  <b-container fluid>
    <admin-preloader :show="loading"/>
    <admin-breadcrumb :items="[{ text: 'Shipping', path: {name: 'admin-shipping'} }, { text: 'Shippers', path: {name: 'admin-shippers'} }]" :name="shipperName" no-title/>
    <b-row>
      <b-col lg="4" md="5">
        <admin-panel second-body with-footer>
          <b-form @submit.prevent="submitForm">
            <b-form-group label="Shipper Name">
              <b-form-input
                  v-model.trim="formData.name"
                  type="text"
                  :state="fieldValid('name')"
                  :disabled="!canUpdateShippers"
              />
              <b-form-invalid-feedback :state="fieldValid('name')">
                {{ formValidation.name }}
              </b-form-invalid-feedback>
            </b-form-group>
            <b-form-group label="License Number">
              <b-form-input
                  v-model.trim="formData.license_number"
                  type="text"
                  :disabled="!canUpdateShippers"
              />
              <b-form-invalid-feedback :state="fieldValid('license_number')">
                {{ formValidation.license_number }}
              </b-form-invalid-feedback>
            </b-form-group>
            <b-form-group label="Email Address">
              <b-form-input
                  v-model.trim="formData.email"
                  type="text"
                  :disabled="!canUpdateShippers"
              />
              <b-form-invalid-feedback :state="fieldValid('email')">
                {{ formValidation.email }}
              </b-form-invalid-feedback>
            </b-form-group>
            <b-form-group label="Phone Number">
              <b-form-input
                  v-model.trim="formData.phone"
                  type="text"
                  placeholder="(###) ###-####"
                  v-mask="'(###) ###-####'"
                  :disabled="!canUpdateShippers"
              />
              <b-form-invalid-feedback :state="fieldValid('phone')">
                {{ formValidation.phone }}
              </b-form-invalid-feedback>
            </b-form-group>
            <div class="text-center">
              <no-permission-wrapper :show="!canUpdateShippers">
                <b-button type="submit" variant="primary" :disabled="loading || !canUpdateShippers">
                  Update Shipper Info
                </b-button>
              </no-permission-wrapper>
            </div>
          </b-form>
          <template v-slot:second-body>
            <small class="text-muted">Created</small>
            <h6>{{ createdOn }}</h6>
            <small class="text-muted">Status</small>
            <p>
              <b-badge v-if="shipper.is_active" variant="success">Active</b-badge>
              <b-badge v-else variant="danger">Inactive</b-badge>
            </p>
            <template v-if="!shipper.is_active">
              <small class="text-muted">Deactivated</small>
              <h6>
                on {{ deactivatedOn }}
                <template v-if="shipper.deactivated_by">
                  <br>by
                  <b-link :to="{name: 'admin-user', params: {id: shipper.deactivated_by.id.toString()}}">
                    {{ shipper.deactivated_by.name }}
                  </b-link>
                </template>
              </h6>
            </template>
          </template>
          <template v-slot:footer>
            <div class="text-center mt-n1">
              <no-permission-wrapper :show="!canDeactivateShippers">
                <b-btn v-if="shipper.is_active" @click.prevent="deactivateShipper" variant="danger" class="mt-1" :disabled="!canDeactivateShippers">Deactivate Shipper</b-btn>
                <b-btn v-else @click="activateShipper" variant="success" class="mt-1" :disabled="!canDeactivateShippers">Reactivate Shipper</b-btn>
              </no-permission-wrapper>
            </div>
          </template>
        </admin-panel>
      </b-col>
      <b-col lg="8" md="7">
        <admin-panel title="Shipper Prices" sub-title="All prices added for this shipper" with-footer>
          <template v-slot:header-right>
            <b-form-select
                class="w-auto mr-2"
                v-model="prices.sorting"
                :options="priceSortingOptions"
                @change="() => loadPrices()"
            />
            <b-form-select
                class="w-auto mr-2"
                v-model="prices.filters.destination_state"
                :options="stateOptions"
                @change="() => loadPrices()"
            />
            <no-permission-wrapper :show="!canUpdateShippers">
              <b-btn variant="success" @click.prevent="openNewPriceModal" class="mb-4" :disabled="!canUpdateShippers">Add New Price</b-btn>
            </no-permission-wrapper>
          </template>
          <b-overlay :show="prices.loading">
            <admin-shipper-prices-table :prices="prices.rows" @update="updatePrice" @delete="deletePrice" no-shipper-col/>
          </b-overlay>
          <template v-slot:footer>
            <b-pagination
                v-model="prices.page"
                :total-rows="prices.total_in_pages"
                :per-page="prices.per_page"
                @change="loadPrices"
                align="center"
                class="admin-pagination mb-0"
            />
          </template>
        </admin-panel>
      </b-col>
    </b-row>
    <admin-shipper-price-new-modal ref="newPriceModal" :shipper-id="id" @created="newPriceCreated"/>
    <admin-shipper-price-edit-modal
        :shipper-price="priceToEdit"
        ref="editPriceModal"
        @updated="priceUpdated"
    />
  </b-container>
</template>

<script>
import AdminPreloader from "../components/AdminPreloader";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import {API_ROUTES} from "../api";
import AdminPanel from "../components/AdminPanel";
import {convertFromUtc} from "../helpers";
import AdminShipperPricesTable from "../components/AdminShipperPricesTable";
import AdminShipperPriceNewModal from "../components/AdminShipperPriceNewModal";
import AdminShipperPriceEditModal from "../components/AdminShipperPriceEditModal";
import {STATES_OPTIONS} from "../constants";
import NoPermissionWrapper from "../components/NoPermissionWrapper.vue";

export default {
  name: "AdminShipper",
  components: {
    NoPermissionWrapper,
    AdminShipperPriceEditModal,
    AdminShipperPriceNewModal,
    AdminShipperPricesTable,
    AdminPanel,
    AdminBreadcrumb,
    AdminPreloader
  },
  props: {
    id: {
      type: String,
      required: true
    }
  },
  data() {
    let stateOptions = [...STATES_OPTIONS];
    stateOptions.unshift({
      value: "",
      text: "All States"
    });

    return {
      loading: true,
      shipper: {},
      formData: {
        name: "",
        license_number: "",
        email: "",
        phone: ""
      },
      formValidation: {},
      prices: {
        loading: true,
        rows: [],
        filters: {
          destination_state: ""
        },
        page: 1,
        per_page: 10,
        total_in_pages: 0,
        sorting: ""
      },
      priceToEdit: {},
      stateOptions
    }
  },
  created() {
    this.$api.get(`${API_ROUTES.ADMIN_SHIPPERS}/${this.id}`).then(({data}) => {
      this.shipper = data.shipper;
    }).catch(({response: {data}}) => {
      this.$bvToast.toast(data.message ?? 'Invalid Request', {
        variant: 'danger',
        title: 'Error!'
      });
      this.$router.replace({name: "admin-users"});
    }).finally(() => {
      this.loading = false;
    });

    this.loadPrices();
  },
  computed: {
    canUpdateShippers() {
      return this.$store.getters['auth/hasAdminPermissions']('shipper_update');
    },
    canDeactivateShippers() {
      return this.$store.getters['auth/hasAdminPermissions']('shipper_deactivate');
    },
    shipperName() {
      if (!this.shipper.id) {
        return "";
      }
      return this.shipper.name + ' - ' + this.shipper.license_number;
    },
    createdOn() {
      if (!this.shipper.id) {
        return "";
      }
      return convertFromUtc(this.shipper.created_at, 'L');
    },
    deactivatedOn() {
      if (!this.shipper.deactivated_at) {
        return "";
      }
      return convertFromUtc(this.shipper.deactivated_at, 'L');
    },
    priceSortingOptions() {
      let options = [
        {
          value: "",
          text: "Default Sorting"
        },
        {
          value: "price_asc",
          text: "Price Up"
        },
        {
          value: "price_desc",
          text: "Price Down"
        },
        {
          value: "destination_state_asc",
          text: "State A to Z"
        },
        {
          value: "destination_state_desc",
          text: "State Z to A"
        },
        {
          value: "destination_city_asc",
          text: "City A to Z"
        },
        {
          value: "destination_city_desc",
          text: "City Z to A"
        },
      ];
      if (this.prices.filters.destination_state) {
        options.splice(options.findIndex(o => o.value === 'destination_state_asc'), 1);
        options.splice(options.findIndex(o => o.value === 'destination_state_desc'), 1);
      }

      return options;
    }
  },
  watch: {
    shipper(shipper) {
      this.formData = {
        name: shipper.name,
        license_number: shipper.license_number,
        email: shipper.email,
        phone: shipper.phone
      };
    },
    'prices.filters.destination_state': function (state) {
      this.$refs.newPriceModal.set('destination_state', state);
      if (state &&
          this.prices.sorting &&
          ['destination_state_asc', 'destination_state_desc'].indexOf(this.prices.sorting) >= 0
      ) {
        this.prices.sorting = "";
        this.loadPrices();
      }
    }
  },
  methods: {
    checkPermission(permission) {
      if (!this[permission]) {
        this.$bvToast.toast('Sorry! You don\'t have permission to do this action', {
          title: 'Denied!',
          variant: 'danger'
        });
        return false;
      }
      return true;
    },
    loadPrices(page = 1) {
      this.prices.loading = true;
      let params = {
        shipper: this.id,
        page: page,
        per_page: this.prices.per_page
      };
      if (this.prices.filters.destination_state) {
        params['destination_state'] = this.prices.filters.destination_state;
      }
      if (this.prices.sorting) {
        params['sorting'] = this.prices.sorting;
      }
      this.$api.get(`${API_ROUTES.ADMIN_SHIPPER_PRICES}`, {
        params
      }).then(({data}) => {
        this.prices.rows = data.shipper_prices;
        this.prices.total_in_pages = data.meta.total;
      }).catch(({response}) => {

      }).finally(() => {
        if (page !== this.prices.page) {
          this.prices.page = page;
        }
        this.prices.loading = false
      });
    },
    fieldValid(field) {
      return typeof this.formValidation[field] !== "undefined" && this.formValidation[field].length > 0 ? false : null;
    },
    submitForm() {
      if (!this.checkPermission('canUpdateShippers')) {
        return;
      }
      this.loading = true;
      this.$api.post(`${API_ROUTES.ADMIN_SHIPPERS}/${this.id}`, this.formData).then(({data}) => {
        this.shipper = data.shipper;
        this.$bvToast.toast('Shipper info has been successfully updated', {
          title: 'Success!',
          variant: 'success'
        });
        this.formValidation = {};
      }).catch(({response: {data}}) => {
        this.$bvToast.toast(data.message ?? 'Failed to process operation', {
          title: 'Error!',
          variant: 'danger'
        });
        let validationErrors = {};
        if (typeof data.errors !== 'undefined') {
          for (let fieldName in data.errors) {
            validationErrors[fieldName] = data.errors[fieldName].join(', ');
          }
        }
        this.formValidation = validationErrors;
      }).finally(() => {
        this.loading = false;
      });
    },
    deactivateShipper() {
      if (!this.checkPermission('canDeactivateShippers')) {
        return;
      }
      this.$bvModal.msgBoxConfirm("You are going to deactivate this shipper. Shipper's prices will be unavailable for calculating shipping price", {
        title: 'Please confirm',
        okVariant: 'success',
        centered: true
      }).then(value => {
        if (value) {
          this.loading = true;
          this.$api.post(`${API_ROUTES.ADMIN_SHIPPERS}/${this.id}/delete`).then(({data}) => {
            this.shipper = data.shipper;
            this.$bvToast.toast('Shipper has been successfully deactivated', {
              title: 'Success!',
              variant: 'success'
            });
            this.formValidation = {};
          }).catch(({response: {data}}) => {
            this.$bvToast.toast(data.message ?? 'Failed to process operation', {
              title: 'Error!',
              variant: 'danger'
            });
          }).finally(() => {
            this.loading = false;
          });
        }
      });
    },
    activateShipper() {
      if (!this.checkPermission('canDeactivateShippers')) {
        return;
      }
      this.$bvModal.msgBoxConfirm("You are going to reactivate this shipper. Shipper's prices will be available for calculating shipping price", {
        title: 'Please confirm',
        okVariant: 'success',
        centered: true
      }).then(value => {
        if (value) {
          this.loading = true;
          this.$api.post(`${API_ROUTES.ADMIN_SHIPPERS}/${this.id}/restore`).then(({data}) => {
            this.shipper = data.shipper;
            this.$bvToast.toast('Shipper has been successfully reactivated', {
              title: 'Success!',
              variant: 'success'
            });
            this.formValidation = {};
          }).catch(({response: {data}}) => {
            this.$bvToast.toast(data.message ?? 'Failed to process operation', {
              title: 'Error!',
              variant: 'danger'
            });
          }).finally(() => {
            this.loading = false;
          });
        }
      });
    },
    openNewPriceModal() {
      if (!this.checkPermission('canUpdateShippers')) {
        return;
      }
      this.$refs.newPriceModal.open();
    },
    newPriceCreated(shipperPrice) {
      this.prices.rows.push(shipperPrice);
      this.$refs.newPriceModal.close();
    },
    updatePrice(shipperPrice) {
      if (!this.checkPermission('canUpdateShippers')) {
        return;
      }
      this.priceToEdit = shipperPrice;
      this.$refs.editPriceModal.open();
    },
    priceUpdated(shipperPrice) {
      let prices = [...this.prices.rows],
          priceIndex = prices.findIndex(price => price.id === shipperPrice.id);
      if (priceIndex >= 0) {
        prices[priceIndex] = shipperPrice;
      } else {
        prices.push(shipperPrice);
      }
      this.prices.rows = prices;
      this.$refs.editPriceModal.close();
    },
    deletePrice(shipperPrice) {
      if (!this.checkPermission('canUpdateShippers')) {
        return;
      }
      this.$bvModal.msgBoxConfirm("You are going to delete this shipper price", {
        title: 'Please confirm',
        okVariant: 'success',
        centered: true
      }).then(value => {
        if (value) {
          this.prices.loading = true;
          this.$api.post(`${API_ROUTES.ADMIN_SHIPPER_PRICES}/${shipperPrice.id}/delete`).then(() => {
            this.$bvToast.toast('Shipper price has been successfully deleted', {
              title: 'Success!',
              variant: 'success'
            });
            let prices = [...this.prices.rows],
                priceIndex = prices.findIndex(price => price.id === shipperPrice.id);
            if (priceIndex >= 0) {
              prices.splice(priceIndex, 1);
            }
            this.prices.rows = prices;
          }).catch(({response: {data}}) => {
            this.$bvToast.toast(data.message ?? 'Failed to process operation', {
              title: 'Error!',
              variant: 'danger'
            });
          }).finally(() => {
            this.prices.loading = false;
          });
        }
      });
    }
  }
}
</script>
